import { PlatformModules } from "@miplatform/types";

const ENTITY = {
  MI_DISPATCH: "MiDispatch",
  MI_SCHEDULING: "MiScheduling",
  MI_ACCOUNTING: "MiAccounting",
  MI_ESTIMATING: "MiEstimating",
  MI_INSPECTIONS: "MiInspections",
  ACCOUNT: "Account",
  APPOINTMENT: "Appointment",
  ATTEMPT: "Attempt",
  BILLING_COMMITMENT: "BillingCommitment",
  BOARDS_CAROUSEL: "BoardsCarousel",
  BOARDS_PINS: "BoardsPins",
  BUILD_TEMPLATE: "BuildTemplate",
  BUILDER: "Builder",
  CALENDAR_BOARDS: "CalendarBoards",
  CALENDAR_SET: "CalendarSet",
  CALENDAR: "Calendar",
  COMMUNITY: "Community",
  DEPARTMENTS: "Department",
  CONTACT: "Contact",
  DOCUMENT_CATEGORY: "DocumentCategory",
  DOCUMENT: "Document",
  DROP: "Drop",
  DROP_PRICE: "DropPrice",
  DROP_TYPE: "DropType",
  FLOOR: "Floor",
  HOME: "Home",
  HR: "HR",
  INSPECTION_TEMPLATE: "InspectionTemplate",
  INSPECTION: "Inspection",
  INVOICE: "Invoice",
  TASK_ISSUE: "TaskIssue",
  TASK_ISSUE_TEMPLATE: "TaskIssueTemplate",
  LABEL_CONFIG: "LabelConfig",
  MILESTONE: "Milestone",
  MOBILE: "Mobile",
  OPERATIONS: "Operations",
  ORDER_TEMPLATE: "OrderTemplate",
  ORDER: "Order",
  WORK_ORDER: "WorkOrder",
  PAYMENT: "Payment",
  PLAN: "Plan",
  PRICING_MATRIX: "PricingMatrix",
  QUALIFICATION: "Qualification",
  QUICKBOOKS: "QuickBooks",
  RESOURCE: "Resource",
  ROLE: "Role",
  RECORDABLE: "Recordable",
  SERVICE_CUSTOMER: "ServiceCustomer",
  SUPPLIER: "Supplier",
  SUBCONTRACTOR: "Subcontractor",
  SUBSCRIPTION: "Subscription",
  SUPPRESS_WORK: "SuppressedWorkTemplateBase",
  SYSTEM_ADMINISTRATION: "SystemAdministration",
  SERVICE_ITEM: "ServiceItem",
  TITLE: "Title",
  TRADE: "Trade",
  USER: "User",
  USER_DAILY_CAPACITY: "UserDailyCapacity",
  WORK_ORDER_TEMPLATE: "WorkOrderTemplate",
  WORK_TEMPLATE_DEPENDENCY_MAP: "WorkTemplateDependencyMap",
  INSPECTIONS: "Inspection",
  CREDIT_MEMO: "CreditMemo",
  WORK_PERMISSION: "RecordPermisson",
  STEP_RESULT: "StepResult",
  REASON: "Reason",
  MATERIAL: "Material",

  // mi-config issue template
  RECORDABLE_TEMPLATE: "RecordableTemplate",
  SERVICE_ITEM_TEMPLATE: "ServiceItemTemplate",
  PUNCH_TEMPLATE: "PunchTemplate",
  INTERNAL_DELAY_TEMPLATE: "InternalDelayTemplate",

  // TODO: Make sure to attach Material to the names here once updated on BE
  MATERIAL_ATTRIBUTE: "Attribute",
  MATERIAL_CATEGORY: "Category",
  // Holiday
  TENANT_HOLIDAY: "TenantHoliday",
};

const TYPE = {
  ASSIGN: "Assign",
  CREATE: "Create",
  DELETE: "Delete",
  DISCONNECT: "Disconnect",
  DUAL_APPROVER: "DualApprover",
  EXPORT: "Export",
  GET_CONFIG: "GetConfig",
  MANAGE_CONFIG: "ManageConfig",
  IMPORT: "Import",
  LOCK: "Lock",
  PREBILL: "Prebill",
  READ: "Read",
  SINGLE_APPROVER: "SingleApprover",
  UPDATE: "Update",
  VIEW: "View",
  VOID: "Void",
  MANAGER: "Manager",
  CLOSE: "Close",
  EXCLUDE_SYNC: "ExcludeSync",
  UNDO: "Undo",
  // Modules
  ACCESS: "Access",
  ADMINISTRATION: "Administration",
  // User
  AVAILABILITY_CREATE: "AvailabilityCreate",
  AVAILABILITY_UPDATE: "AvailabilityUpdate",
  AVAILABILITY_DELETE: "AvailabilityDelete",
  // Order
  APPROVE: "Approve",
  CONFIRM: "Confirm",
  CANCEL: "Cancel",
  APPROVE_OVERRIDE: "ApproveOverride",
  FORCE_COMPLETE: "ForceComplete",
  FORCE_COMPLETE_FOR_ACCOUNTING: "ForceCompleteForAccounting",
  OVERRIDE_HOLD_SETTING: "OverrideHoldSetting",
  SCHEDULE_ON_HOLIDAY: "ScheduleOnHoliday",
  SCHEDULE: "Schedule",
  PERFORM_WORK: "PerformWork",
  // Attempt
  RESCHEDULE_LOCK: "RescheduleLock",
  WORK: "Work",
  // Invoice
  CREATE_VARIANCES: "CreateVariances",
  INVOICE_NOT_TODAY: "InvoiceNotToday",
  // Payment
  MANAGER_OVERRIDE_UPDATE: "ManagerOverrideUpdate",
  // Credit Memos
  MANAGE_OVERRIDE: "ManageOverride",
  // Calendar
  VIEW_ALL_BOARDS: "ViewAllBoards",
  // Order Template
  MANAGE_COST_CODES: "ManageCostCodes",
};

const entityWithPermissions = {
  [ENTITY.ATTEMPT]: [
    TYPE.CREATE,
    TYPE.UPDATE,
    TYPE.APPROVE_OVERRIDE,
    TYPE.RESCHEDULE_LOCK,
    TYPE.WORK,
  ],
  [ENTITY.APPOINTMENT]: [TYPE.LOCK],
  [ENTITY.ACCOUNT]: [TYPE.CREATE, TYPE.UPDATE, TYPE.READ],
  [ENTITY.BUILD_TEMPLATE]: [TYPE.CREATE, TYPE.UPDATE, TYPE.DELETE],
  [ENTITY.BUILDER]: [TYPE.CREATE, TYPE.UPDATE, TYPE.READ, TYPE.DELETE],
  [ENTITY.BOARDS_CAROUSEL]: [TYPE.READ],
  [ENTITY.BOARDS_PINS]: [TYPE.READ],
  [ENTITY.CONTACT]: [TYPE.CREATE, TYPE.UPDATE, TYPE.DELETE],
  [ENTITY.COMMUNITY]: [TYPE.CREATE, TYPE.UPDATE],
  [ENTITY.DOCUMENT]: [TYPE.CREATE, TYPE.UPDATE, TYPE.DELETE, TYPE.READ],
  [ENTITY.DOCUMENT_CATEGORY]: [TYPE.CREATE, TYPE.UPDATE, TYPE.DELETE],
  [ENTITY.DROP]: [TYPE.CREATE, TYPE.UPDATE, TYPE.READ, TYPE.DELETE],
  [ENTITY.DROP_TYPE]: [TYPE.CREATE, TYPE.UPDATE, TYPE.READ, TYPE.DELETE],
  [ENTITY.DROP_PRICE]: [TYPE.READ, TYPE.UPDATE, TYPE.CREATE, TYPE.DELETE],
  [ENTITY.FLOOR]: [TYPE.CREATE, TYPE.UPDATE, TYPE.READ, TYPE.DELETE],
  [ENTITY.HOME]: [TYPE.CREATE, TYPE.UPDATE, TYPE.READ, TYPE.CLOSE],
  [ENTITY.TASK_ISSUE]: [TYPE.CREATE, TYPE.UPDATE, TYPE.READ, TYPE.DELETE],
  [ENTITY.TASK_ISSUE_TEMPLATE]: [TYPE.CREATE, TYPE.UPDATE, TYPE.DELETE],
  [ENTITY.LABEL_CONFIG]: [TYPE.CREATE, TYPE.UPDATE, TYPE.DELETE],
  [ENTITY.MILESTONE]: [TYPE.CREATE, TYPE.UPDATE, TYPE.DELETE],
  [ENTITY.ORDER_TEMPLATE]: [
    TYPE.CREATE,
    TYPE.UPDATE,
    TYPE.DELETE,
    TYPE.MANAGE_COST_CODES,
  ],
  [ENTITY.INSPECTION_TEMPLATE]: [TYPE.CREATE, TYPE.UPDATE, TYPE.DELETE],
  [ENTITY.WORK_ORDER_TEMPLATE]: [
    TYPE.READ,
    TYPE.CREATE,
    TYPE.UPDATE,
    TYPE.DELETE,
  ],
  [ENTITY.WORK_TEMPLATE_DEPENDENCY_MAP]: [
    TYPE.READ,
    TYPE.CREATE,
    TYPE.UPDATE,
    TYPE.DELETE,
  ],
  [ENTITY.ORDER]: [
    TYPE.CREATE,
    TYPE.UPDATE,
    TYPE.READ,
    TYPE.PREBILL,
    TYPE.ASSIGN,
    TYPE.VOID,
    TYPE.FORCE_COMPLETE,
    TYPE.FORCE_COMPLETE_FOR_ACCOUNTING,
    TYPE.OVERRIDE_HOLD_SETTING,
    TYPE.APPROVE,
    TYPE.CONFIRM,
    TYPE.CANCEL,
    TYPE.PERFORM_WORK,
    TYPE.SCHEDULE,
    TYPE.SCHEDULE_ON_HOLIDAY,
    TYPE.UNDO,
  ],
  [ENTITY.PLAN]: [
    TYPE.CREATE,
    TYPE.UPDATE,
    TYPE.READ,
    TYPE.DELETE,
    TYPE.IMPORT,
  ],
  [ENTITY.QUALIFICATION]: [TYPE.CREATE, TYPE.UPDATE, TYPE.READ, TYPE.DELETE],
  [ENTITY.RESOURCE]: [TYPE.CREATE, TYPE.UPDATE, TYPE.READ, TYPE.DELETE],
  [ENTITY.ROLE]: [TYPE.UPDATE, TYPE.READ, TYPE.DELETE, TYPE.CREATE],
  [ENTITY.SUBSCRIPTION]: [TYPE.CREATE, TYPE.READ],
  [ENTITY.TITLE]: [TYPE.UPDATE, TYPE.READ, TYPE.CREATE, TYPE.DELETE],
  [ENTITY.TRADE]: [TYPE.CREATE, TYPE.UPDATE, TYPE.READ, TYPE.DELETE],
  [ENTITY.USER]: [
    TYPE.CREATE,
    TYPE.UPDATE,
    TYPE.DELETE,
    TYPE.READ,
    TYPE.AVAILABILITY_CREATE,
    TYPE.AVAILABILITY_UPDATE,
    TYPE.AVAILABILITY_DELETE,
  ],
  [ENTITY.USER_DAILY_CAPACITY]: [
    TYPE.CREATE,
    TYPE.UPDATE,
    TYPE.READ,
    TYPE.DELETE,
  ],
  [ENTITY.CALENDAR]: [TYPE.CREATE, TYPE.UPDATE, TYPE.DELETE],
  [ENTITY.CALENDAR_SET]: [TYPE.CREATE, TYPE.UPDATE, TYPE.DELETE],
  [ENTITY.CALENDAR_BOARDS]: [
    TYPE.CREATE,
    TYPE.UPDATE,
    TYPE.READ,
    TYPE.DELETE,
    TYPE.VIEW_ALL_BOARDS,
  ],
  [ENTITY.QUICKBOOKS]: [TYPE.MANAGE_CONFIG, TYPE.EXCLUDE_SYNC],
  [ENTITY.INVOICE]: [
    TYPE.CREATE,
    TYPE.UPDATE,
    TYPE.READ,
    TYPE.DELETE,
    TYPE.VOID,
    TYPE.CREATE_VARIANCES,
    TYPE.INVOICE_NOT_TODAY,
  ],
  [ENTITY.BILLING_COMMITMENT]: [
    TYPE.CREATE,
    TYPE.UPDATE,
    TYPE.READ,
    TYPE.DELETE,
    TYPE.VOID,
    TYPE.MANAGE_OVERRIDE,
  ],
  [ENTITY.PRICING_MATRIX]: [
    TYPE.CREATE,
    TYPE.READ,
    TYPE.UPDATE,
    TYPE.DELETE,
    TYPE.SINGLE_APPROVER,
    TYPE.DUAL_APPROVER,
  ],
  [ENTITY.PAYMENT]: [
    TYPE.CREATE,
    TYPE.READ,
    TYPE.UPDATE,
    TYPE.MANAGER_OVERRIDE_UPDATE,
  ],
  [ENTITY.SERVICE_CUSTOMER]: [TYPE.CREATE, TYPE.UPDATE, TYPE.READ, TYPE.DELETE],
  [ENTITY.SUPPLIER]: [TYPE.CREATE, TYPE.UPDATE, TYPE.READ, TYPE.DELETE],
  [ENTITY.SUBCONTRACTOR]: [TYPE.CREATE, TYPE.UPDATE, TYPE.READ, TYPE.DELETE],
  [ENTITY.SUPPRESS_WORK]: [TYPE.CREATE, TYPE.READ, TYPE.DELETE],
  [ENTITY.SERVICE_ITEM]: [TYPE.CREATE, TYPE.UPDATE, TYPE.READ, TYPE.DELETE],
  [ENTITY.DEPARTMENTS]: [TYPE.CREATE, TYPE.UPDATE, TYPE.READ, TYPE.DELETE],
  // Modules
  [ENTITY.MI_DISPATCH]: [TYPE.ACCESS, TYPE.ADMINISTRATION],
  [ENTITY.MI_SCHEDULING]: [TYPE.ACCESS, TYPE.ADMINISTRATION],
  [ENTITY.MI_ACCOUNTING]: [TYPE.ACCESS, TYPE.ADMINISTRATION],
  [ENTITY.MI_ESTIMATING]: [TYPE.ACCESS, TYPE.ADMINISTRATION],
  [ENTITY.MI_INSPECTIONS]: [TYPE.ACCESS, TYPE.ADMINISTRATION],
  [ENTITY.HR]: [TYPE.ADMINISTRATION],
  [ENTITY.OPERATIONS]: [TYPE.ADMINISTRATION],
  [ENTITY.SYSTEM_ADMINISTRATION]: [TYPE.ADMINISTRATION],
  [ENTITY.MOBILE]: [TYPE.ACCESS, TYPE.ADMINISTRATION],
  [ENTITY.CREDIT_MEMO]: [
    TYPE.CREATE,
    TYPE.READ,
    TYPE.UPDATE,
    TYPE.MANAGE_OVERRIDE,
    TYPE.VOID,
  ],
  [ENTITY.INSPECTIONS]: [
    TYPE.CREATE,
    TYPE.READ,
    TYPE.UPDATE,
    TYPE.DELETE,
    TYPE.ASSIGN,
    TYPE.SCHEDULE,
    TYPE.PERFORM_WORK,
  ],
  [ENTITY.WORK_PERMISSION]: [TYPE.UPDATE],
  [ENTITY.STEP_RESULT]: [TYPE.CREATE, TYPE.READ, TYPE.UPDATE, TYPE.DELETE],
  [ENTITY.REASON]: [TYPE.CREATE, TYPE.READ, TYPE.UPDATE, TYPE.DELETE],
  [ENTITY.RECORDABLE]: [TYPE.CREATE, TYPE.READ, TYPE.UPDATE, TYPE.DELETE],
  [ENTITY.MATERIAL]: [TYPE.CREATE, TYPE.READ, TYPE.UPDATE, TYPE.DELETE],
  [ENTITY.MATERIAL_ATTRIBUTE]: [
    TYPE.CREATE,
    TYPE.READ,
    TYPE.UPDATE,
    TYPE.DELETE,
  ],
  [ENTITY.MATERIAL_CATEGORY]: [
    TYPE.CREATE,
    TYPE.READ,
    TYPE.UPDATE,
    TYPE.DELETE,
  ],
  [ENTITY.WORK_ORDER]: [
    TYPE.READ,
    TYPE.UPDATE,
    TYPE.CREATE,
    TYPE.ASSIGN,
    TYPE.SCHEDULE,
    TYPE.PERFORM_WORK,
  ],
  [ENTITY.RECORDABLE_TEMPLATE]: [
    TYPE.READ,
    TYPE.CREATE,
    TYPE.UPDATE,
    TYPE.DELETE,
  ],
  [ENTITY.SERVICE_ITEM_TEMPLATE]: [
    TYPE.READ,
    TYPE.CREATE,
    TYPE.UPDATE,
    TYPE.DELETE,
  ],
  [ENTITY.PUNCH_TEMPLATE]: [TYPE.READ, TYPE.CREATE, TYPE.UPDATE, TYPE.DELETE],
  [ENTITY.INTERNAL_DELAY_TEMPLATE]: [
    TYPE.READ,
    TYPE.CREATE,
    TYPE.UPDATE,
    TYPE.DELETE,
  ],
  [ENTITY.TASK_ISSUE_TEMPLATE]: [
    TYPE.READ,
    TYPE.CREATE,
    TYPE.UPDATE,
    TYPE.DELETE,
  ],
  [ENTITY.TENANT_HOLIDAY]: [TYPE.READ, TYPE.CREATE, TYPE.UPDATE, TYPE.DELETE],
};

type EntityKey = keyof typeof ENTITY;
type TypeKey = keyof typeof TYPE;

const TYPE_REVERSE_MAP = Object.fromEntries(
  Object.entries(TYPE).map(([key, value]) => [value, key])
);

const buildPermissions = () =>
  Object.keys(ENTITY).reduce((permissions, entityKey) => {
    const entityValue = ENTITY[entityKey as EntityKey];
    const types = entityWithPermissions[entityValue] || [];

    permissions[entityKey as EntityKey] = types.reduce(
      (entityPermissions: Record<TypeKey, string>, type) => {
        // Look up the key for the type value using the reverse map example (AvailabilityUpdate => AVAILABILITY_UPDATE)
        const actualKey = TYPE_REVERSE_MAP[type] as TypeKey;

        if (actualKey) {
          entityPermissions[actualKey] = `${entityValue}:${type}`;
        }
        return entityPermissions;
      },
      {} as Record<TypeKey, string>
    );

    return permissions;
  }, {} as Record<EntityKey, Record<TypeKey, string>>);

const buildPermissionsString = () => {
  return Object.values(ENTITY)
    .flatMap((entity) =>
      (entityWithPermissions[entity] || []).map((type) => `${entity}:${type}`)
    )
    .join(",");
};

export const PERMISSIONS_STRING = buildPermissionsString();
export const PERMISSIONS = buildPermissions();

export const MODULE_PERMISSION_MAP: Record<
  PlatformModules,
  { module: PlatformModules; access: string; administration: string }
> = {
  [PlatformModules.MiDispatch]: {
    module: PlatformModules.MiDispatch,
    access: PERMISSIONS.MI_DISPATCH.ACCESS,
    administration: PERMISSIONS.MI_DISPATCH.ADMINISTRATION,
  },
  [PlatformModules.MiScheduling]: {
    module: PlatformModules.MiScheduling,
    access: PERMISSIONS.MI_SCHEDULING.ACCESS,
    administration: PERMISSIONS.MI_SCHEDULING.ADMINISTRATION,
  },
  [PlatformModules.MiAccounting]: {
    module: PlatformModules.MiAccounting,
    access: PERMISSIONS.MI_ACCOUNTING.ACCESS,
    administration: PERMISSIONS.MI_ACCOUNTING.ADMINISTRATION,
  },
  [PlatformModules.MiEstimating]: {
    module: PlatformModules.MiEstimating,
    access: PERMISSIONS.MI_ESTIMATING.ACCESS,
    administration: PERMISSIONS.MI_ESTIMATING.ADMINISTRATION,
  },
  [PlatformModules.MiInspections]: {
    module: PlatformModules.MiInspections,
    access: PERMISSIONS.MI_INSPECTIONS.ACCESS,
    administration: PERMISSIONS.MI_INSPECTIONS.ADMINISTRATION,
  },
};
